import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import DatePicker from 'react-datepicker';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiSearch, FiX } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import { CalendarSvg } from '../../../assets/svg-icon-components/CalendarSvg';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { RhCard } from '../../core/RhCard';
import { RhInput } from '../../core/RhInput';
import { RhSelect, RhSelectOption } from '../../core/RhSelect';
import { colors, H2, H3, Regular } from '../../core/Styles';
import { DonationsProps } from '../containers/DonationsContainer';
import {
  AmountText,
  BackButton,
  ChurchListItem,
  ChurchListItemIcon,
  ChurchNameHeader,
  ClearDateIconButton,
  CustomDateInput,
  DateFilterWrapper,
  DatePickerStyles,
  DateRangeWrapper,
  DateSeparator,
  Ellipsis,
  EmptyStateDiv,
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItem,
  ListItemColumn,
  ListItemHorizontalDivider,
  PageInfo,
  PageNumberButton,
  PageNumbers,
  PaginationButton,
  PaginationContainer,
  StatusFilterWrapper,
  StatusBadge,
  TotalSection,
} from './DonationsScreen.styles';

export const DonationsScreen: React.FC<DonationsProps> = props => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusColor = (status?: string) => {
    const statusLower = status?.toLowerCase() || 'processing';
    if (statusLower === 'completed' || statusLower === 'success' || statusLower === 'succeeded') {
      return '#10b981'; // Green
    }
    if (statusLower === 'failed' || statusLower === 'error' || statusLower === 'cancelled') {
      return '#ef4444'; // Red
    }
    return '#f59e0b'; // Yellow for Processing/Pending
  };

  const getStatusText = (status?: string) => {
    const statusLower = status?.toLowerCase() || 'processing';
    if (statusLower === 'completed' || statusLower === 'success' || statusLower === 'succeeded') {
      return 'Succeeded';
    }
    if (statusLower === 'failed' || statusLower === 'error' || statusLower === 'cancelled') {
      return 'Failed';
    }
    return 'Processing';
  };

  return (
    <DashboardWrapper>
      <DashboardHeader title="Donations" />
      <DatePickerStyles>
        <RhCard>
          {props.isSystemAdmin && !props.selectedChurchId ? (
            // Church list search
            <HorizontalContent
              left={<H2>Churches</H2>}
              right={[
                props.isLoadingDonations && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
                <RhInput
                  key="search-box"
                  onChange={e => {
                    props.userUpdatedSearchTerm(e.target.value);
                  }}
                  value={props.searchTerm}
                  icon={FiSearch}
                  placeholder="Search by church name"
                />,
              ]}
            />
          ) : // Donation listing - all filters on right side
          props.isSystemAdmin && props.selectedChurchId ? (
            // Church detail view - Back button aligned with filters
            <HorizontalContent
              left={
                <BackButton onClick={props.userClickedBack}>
                  <FiArrowLeft size={16} />
                  <Regular>Back to Churches</Regular>
                </BackButton>
              }
              right={[
                props.isLoadingDonations && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
                <RhInput
                  key="search-box"
                  onChange={e => {
                    props.userUpdatedSearchTerm(e.target.value);
                  }}
                  value={props.searchTerm}
                  icon={FiSearch}
                  placeholder="Search by email"
                />,
                <StatusFilterWrapper key="status-filter">
                  <RhSelect
                    value={{
                      value: props.statusFilter,
                      label:
                        props.statusFilter === 'all'
                          ? 'All'
                          : props.statusFilter.charAt(0).toUpperCase() + props.statusFilter.slice(1),
                    }}
                    onChange={(option: RhSelectOption) => {
                      props.userUpdatedStatusFilter(option.value);
                    }}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'processing', label: 'Processing' },
                      { value: 'failed', label: 'Failed' },
                    ]}
                    placeholder="Status"
                  />
                </StatusFilterWrapper>,
                <DateRangeWrapper key="date-range">
                  <DateFilterWrapper>
                    <CalendarSvg width={16} height={16} />
                    <DatePicker
                      selected={props.fromDate}
                      onChange={(date: Date | null) => props.userUpdatedFromDate(date)}
                      selectsStart
                      startDate={props.fromDate}
                      endDate={props.toDate}
                      maxDate={new Date()}
                      customInput={<CustomDateInput placeholder="From" />}
                      dateFormat="MMM dd, yyyy"
                      popperPlacement="bottom-end"
                    />
                  </DateFilterWrapper>
                  <DateSeparator>to</DateSeparator>
                  <DateFilterWrapper>
                    <CalendarSvg width={16} height={16} />
                    <DatePicker
                      selected={props.toDate}
                      onChange={(date: Date | null) => props.userUpdatedToDate(date)}
                      selectsEnd
                      startDate={props.fromDate}
                      endDate={props.toDate}
                      minDate={props.fromDate}
                      maxDate={new Date()}
                      customInput={<CustomDateInput placeholder="To" />}
                      dateFormat="MMM dd, yyyy"
                      popperPlacement="bottom-end"
                    />
                  </DateFilterWrapper>
                  {(props.fromDate || props.toDate) && (
                    <ClearDateIconButton
                      onClick={() => {
                        props.userUpdatedFromDate(null);
                        props.userUpdatedToDate(null);
                      }}
                      title="Clear dates">
                      <FiX size={16} />
                    </ClearDateIconButton>
                  )}
                </DateRangeWrapper>,
              ]}
            />
          ) : (
            // Regular donation listing - Donations title with filters
            <HorizontalContent
              left={<H2>Donations</H2>}
              right={[
                props.isLoadingDonations && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
                <RhInput
                  key="search-box"
                  onChange={e => {
                    props.userUpdatedSearchTerm(e.target.value);
                  }}
                  value={props.searchTerm}
                  icon={FiSearch}
                  placeholder="Search by email"
                />,
                <StatusFilterWrapper key="status-filter">
                  <RhSelect
                    value={{
                      value: props.statusFilter,
                      label:
                        props.statusFilter === 'all'
                          ? 'All'
                          : props.statusFilter.charAt(0).toUpperCase() + props.statusFilter.slice(1),
                    }}
                    onChange={(option: RhSelectOption) => {
                      props.userUpdatedStatusFilter(option.value);
                    }}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'processing', label: 'Processing' },
                      { value: 'failed', label: 'Failed' },
                    ]}
                    placeholder="Status"
                  />
                </StatusFilterWrapper>,
                <DateRangeWrapper key="date-range">
                  <DateFilterWrapper>
                    <CalendarSvg width={16} height={16} />
                    <DatePicker
                      selected={props.fromDate}
                      onChange={(date: Date | null) => props.userUpdatedFromDate(date)}
                      selectsStart
                      startDate={props.fromDate}
                      endDate={props.toDate}
                      maxDate={new Date()}
                      customInput={<CustomDateInput placeholder="From" />}
                      dateFormat="MMM dd, yyyy"
                      popperPlacement="bottom-end"
                    />
                  </DateFilterWrapper>
                  <DateSeparator>to</DateSeparator>
                  <DateFilterWrapper>
                    <CalendarSvg width={16} height={16} />
                    <DatePicker
                      selected={props.toDate}
                      onChange={(date: Date | null) => props.userUpdatedToDate(date)}
                      selectsEnd
                      startDate={props.fromDate}
                      endDate={props.toDate}
                      minDate={props.fromDate}
                      maxDate={new Date()}
                      customInput={<CustomDateInput placeholder="To" />}
                      dateFormat="MMM dd, yyyy"
                      popperPlacement="bottom-end"
                    />
                  </DateFilterWrapper>
                  {(props.fromDate || props.toDate) && (
                    <ClearDateIconButton
                      onClick={() => {
                        props.userUpdatedFromDate(null);
                        props.userUpdatedToDate(null);
                      }}
                      title="Clear dates">
                      <FiX size={16} />
                    </ClearDateIconButton>
                  )}
                </DateRangeWrapper>,
              ]}
            />
          )}

          {props.isSystemAdmin ? (
            // System Admin view: Church list or church detail
            props.selectedChurchId ? (
              // Church detail view with pagination
              <div>
                <ChurchNameHeader>
                  <H3>{props.donationsByChurch[props.selectedChurchId]?.churchName}</H3>
                  <Regular weight="600" color={colors.primary}>
                    Total: {formatCurrency(props.donationsByChurch[props.selectedChurchId]?.totalAmount || 0)}
                  </Regular>
                </ChurchNameHeader>
                <List>
                  <ListHeader>
                    <ListHeaderItem span={10}>Email</ListHeaderItem>
                    <ListHeaderItem span={10}>Stripe Id</ListHeaderItem>
                    <ListHeaderItem span={10}>Amount</ListHeaderItem>
                    <ListHeaderItem span={10}>Date</ListHeaderItem>
                    <ListHeaderItem span={10}>Status</ListHeaderItem>
                  </ListHeader>
                  <ListBody>
                    {props.paginatedDonations.length === 0 ? (
                      <EmptyStateDiv>
                        <Regular>No donations found</Regular>
                      </EmptyStateDiv>
                    ) : (
                      props.paginatedDonations.map(donation => (
                        <div key={donation.id}>
                          <ListItem>
                            <ListItemColumn span={10}>
                              <span>{donation?.emailId || '-'}</span>
                            </ListItemColumn>
                            <ListItemColumn span={10}>
                              <span>{donation.stripeId || '-'}</span>
                            </ListItemColumn>
                            <ListItemColumn span={10}>
                              <AmountText color={getStatusColor(donation.status)}>
                                {formatCurrency(donation.amount)}
                              </AmountText>
                            </ListItemColumn>
                            <ListItemColumn span={10}>
                              <span>{dayjs(donation.date).format('MMMM DD, YYYY')}</span>
                            </ListItemColumn>
                            <ListItemColumn span={10}>
                              <StatusBadge status={donation.status}>{getStatusText(donation.status)}</StatusBadge>
                            </ListItemColumn>
                          </ListItem>
                          <ListItemHorizontalDivider />
                        </div>
                      ))
                    )}
                  </ListBody>
                </List>
                {props.totalPages > 0 && (
                  <PaginationContainer>
                    <PaginationButton
                      onClick={() => props.userChangedPage(props.currentPage - 1)}
                      disabled={props.currentPage === 1}
                      title="Previous page">
                      <FiChevronLeft size={18} />
                    </PaginationButton>
                    <PageNumbers>
                      {Array.from({ length: props.totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first page, last page, current page, and pages around current
                          if (page === 1 || page === props.totalPages) return true;
                          if (Math.abs(page - props.currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, array) => {
                          // Add ellipsis if there's a gap
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && <Ellipsis>...</Ellipsis>}
                              <PageNumberButton
                                onClick={() => props.userChangedPage(page)}
                                active={page === props.currentPage}>
                                {page}
                              </PageNumberButton>
                            </React.Fragment>
                          );
                        })}
                    </PageNumbers>
                    <PaginationButton
                      onClick={() => props.userChangedPage(props.currentPage + 1)}
                      disabled={props.currentPage === props.totalPages}
                      title="Next page">
                      <FiChevronRight size={18} />
                    </PaginationButton>
                    <PageInfo>
                      <Regular color={colors.grey}>
                        {props.currentPage} of {props.totalPages}
                      </Regular>
                    </PageInfo>
                  </PaginationContainer>
                )}
              </div>
            ) : (
              // Church list view
              <List>
                <ListHeader>
                  <ListHeaderItem span={15}>Church Name</ListHeaderItem>
                  <ListHeaderItem span={10}>Total Amount</ListHeaderItem>
                  <ListHeaderItem span={10}>Donation Count</ListHeaderItem>
                </ListHeader>
                <ListBody>
                  {props.filteredChurches.length === 0 ? (
                    <EmptyStateDiv>
                      <Regular>No churches found</Regular>
                    </EmptyStateDiv>
                  ) : (
                    props.filteredChurches.map(church => (
                      <div key={church.churchId}>
                        <ChurchListItem onClick={() => props.userClickedChurch(church.churchId)}>
                          <ListItemColumn span={15}>
                            <span>{church.churchName}</span>
                          </ListItemColumn>
                          <ListItemColumn span={10}>
                            <span>{formatCurrency(church.totalAmount)}</span>
                          </ListItemColumn>
                          <ListItemColumn span={10}>
                            <span>
                              {church.totalDonations} donation{church.totalDonations !== 1 ? 's' : ''}
                            </span>
                          </ListItemColumn>
                          <ChurchListItemIcon>
                            <FiChevronRight size={20} />
                          </ChurchListItemIcon>
                        </ChurchListItem>
                        <ListItemHorizontalDivider />
                      </div>
                    ))
                  )}
                </ListBody>
              </List>
            )
          ) : (
            // Church Admin view: List of users who made donations with pagination
            <div>
              <List>
                <ListHeader>
                  <ListHeaderItem span={10}>Email</ListHeaderItem>
                  <ListHeaderItem span={10}>Amount</ListHeaderItem>
                  <ListHeaderItem span={10}>Date</ListHeaderItem>
                  <ListHeaderItem span={10}>Status</ListHeaderItem>
                </ListHeader>
                <ListBody>
                  {props.paginatedDonations.length === 0 ? (
                    <EmptyStateDiv>
                      <Regular>No donations found</Regular>
                    </EmptyStateDiv>
                  ) : (
                    props.paginatedDonations.map(donation => (
                      <div key={donation.id}>
                        <ListItem>
                          <ListItemColumn span={10}>
                            <span>{donation.emailId || '-'}</span>
                          </ListItemColumn>
                          <ListItemColumn span={10}>
                            <AmountText color={getStatusColor(donation.status)}>
                              {formatCurrency(donation.amount)}
                            </AmountText>
                          </ListItemColumn>
                          <ListItemColumn span={10}>
                            <span>{dayjs(donation.date).format('MMMM DD, YYYY')}</span>
                          </ListItemColumn>
                          <ListItemColumn span={10}>
                            <StatusBadge status={donation.status}>{getStatusText(donation.status)}</StatusBadge>
                          </ListItemColumn>
                        </ListItem>
                        <ListItemHorizontalDivider />
                      </div>
                    ))
                  )}
                </ListBody>
              </List>
              {props.donations.length > 0 && (
                <TotalSection>
                  <Regular weight="600">Total: {formatCurrency(_.sumBy(props.donations, 'amount'))}</Regular>
                </TotalSection>
              )}
              {props.totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => props.userChangedPage(props.currentPage - 1)}
                    disabled={props.currentPage === 1}
                    title="Previous page">
                    <FiChevronLeft size={18} />
                  </PaginationButton>
                  <PageNumbers>
                    {Array.from({ length: props.totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first page, last page, current page, and pages around current
                        if (page === 1 || page === props.totalPages) return true;
                        if (Math.abs(page - props.currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && <Ellipsis>...</Ellipsis>}
                            <PageNumberButton
                              onClick={() => props.userChangedPage(page)}
                              active={page === props.currentPage}>
                              {page}
                            </PageNumberButton>
                          </React.Fragment>
                        );
                      })}
                  </PageNumbers>
                  <PaginationButton
                    onClick={() => props.userChangedPage(props.currentPage + 1)}
                    disabled={props.currentPage === props.totalPages}
                    title="Next page">
                    <FiChevronRight size={18} />
                  </PaginationButton>
                  <PageInfo>
                    <Regular color={colors.grey}>
                      {props.currentPage} of {props.totalPages}
                    </Regular>
                  </PageInfo>
                </PaginationContainer>
              )}
            </div>
          )}
        </RhCard>
      </DatePickerStyles>
    </DashboardWrapper>
  );
};
